import { Button, Card, Col, DatePicker, Input, TimePicker, Row, Space, Table, Typography, List, Empty } from 'antd';
import moment, { Moment } from 'moment';
import * as React from 'react';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import TimeZoneSelect from '../../components/TimezoneSelect';
import { MeetingSearch } from '../../models/meeting-interface';
import { TimeZone } from '../../models/time-zone-interface';
import { CustomerServiceQueries } from '../../queries/customer-service-queries';
import { DateRangeOverlaps, GetDateFormateStr } from '../../utilites/dateTimeHelper';
import AvailableTimeCard from './components/AvailableTimeCard';

const { Title } = Typography;
const { RangePicker } = TimePicker;
type RangeValue = Parameters<NonNullable<React.ComponentProps<typeof DatePicker.RangePicker>['onChange']>>[0]

function giveUtc(start) {
  var t = moment().format("YYYY-MM-DD")
  var t1 = t + " " + start
  return moment(t1, "YYYY-MM-DD h:mm A").format()
}

function MeetingsPage() {
    const [timeZoneId, setTimeZoneId] = useState<number>(6);
    const [timeZoneName, setTimeZoneName] = useState<string | null>(null);
    const [selectedDate, setSelectedDates] = useState<Moment>(moment().startOf('day'));
    const [selectedTimes, setSelectedTimes] = useState<RangeValue>(null);
    const [companySearch, setCompanySearch] = useState<string | null>(null);
    const [filteredMeetings, setFilteredMeetings] = useState<MeetingSearch[]>([]);
    const [availableTimes, setAvailableTimes] = useState<RangeValue[]>([]);
    const meetingsQuery = CustomerServiceQueries.useMeetingListForDate(selectedDate, timeZoneId);

    useEffect(() => {
      if(!meetingsQuery?.isLoading) {
        let meetings = meetingsQuery?.data ?? [];

        if(!!companySearch) {
          meetings = meetings?.filter(m => m.companyName?.toLowerCase().includes(companySearch.toLowerCase()));
        }

        if(!!selectedTimes) {
          meetings = meetings?.filter(m => DateRangeOverlaps(moment(m.startTime).toDate(), !!m.endTime ? moment(m.endTime).toDate() : moment(m.endTime).endOf('day').toDate(), 
            new Date(`${moment(selectedDate).format(GetDateFormateStr(1))} ${moment(selectedTimes[0].toISOString()).toDate().toLocaleTimeString()}`), 
            new Date(`${moment(selectedDate).format(GetDateFormateStr(1))} ${moment(selectedTimes[1].toISOString()).toDate().toLocaleTimeString()}`)
          ));
        }

        setFilteredMeetings(meetings);
      } else {
        setFilteredMeetings([]);
      }
    }, [meetingsQuery?.data, companySearch, selectedTimes])

    useEffect(() => {
      const availableTimeArray: RangeValue[] = [];
      let sHour = 0;
      let sMin = 0;
      let eHour = 23;
      let eMin = 59;

      if(!!selectedTimes && selectedTimes?.length > 1) {
        sHour = selectedTimes[0].hour() ?? 0;
        sMin = selectedTimes[0].minute() ?? 0;
        eHour = selectedTimes[1].hour() ?? 23;
        eMin = selectedTimes[1].minute() ?? 59;
      }

      var x = 15; //minutes interval
      var times: number[] = []; // time array
      var tt = (sHour * 60) + sMin; // start time

      let currentStart = new Date(`${moment(selectedDate).format(GetDateFormateStr(1))} ${moment(selectedDate).startOf('day').add(tt, 'minutes').toDate().toLocaleTimeString()}`);
      let currentEnd = new Date(`${moment(selectedDate).format(GetDateFormateStr(1))} ${moment(selectedDate).startOf('day').add(tt + x, 'minutes').toDate().toLocaleTimeString()}`)
      let isCurrentlyAvailable = false;
      //loop to increment the time and push results in array
      for (var i=0;tt<(eHour*60) + eMin; i++) {
        times[i] = filteredMeetings?.some(m => DateRangeOverlaps(moment(m.startTime).toDate(), !!m.endTime ? moment(m.endTime).toDate() : moment(m.endTime).endOf('day').toDate(), 
        new Date(`${moment(selectedDate).format(GetDateFormateStr(1))} ${moment(selectedDate).startOf('day').add(tt, 'minutes').toDate().toLocaleTimeString()}`), 
        new Date(`${moment(selectedDate).format(GetDateFormateStr(1))} ${moment(selectedDate).startOf('day').add(tt + x, 'minutes').toDate().toLocaleTimeString()}`)
        )) ? 0 : 1;

        if(times[i] == 1 && tt + x<(eHour*60) + eMin) {
          if(!isCurrentlyAvailable) {
            isCurrentlyAvailable = true;

            currentStart = new Date(`${moment(selectedDate).format(GetDateFormateStr(1))} ${moment(selectedDate).startOf('day').add(tt, 'minutes').toDate().toLocaleTimeString()}`);
          }
        } else {
          if(isCurrentlyAvailable) {
            isCurrentlyAvailable = false;
            currentEnd = new Date(`${moment(selectedDate).format(GetDateFormateStr(1))} ${moment(selectedDate).startOf('day').add(tt + (times[i] == 1 ? x - 1 : 0), 'minutes').toDate().toLocaleTimeString()}`);

            availableTimeArray.push([moment(currentStart), moment(currentEnd)]);
          }
        }

        tt = tt + x;
      }

      setAvailableTimes(availableTimeArray);
    }, [filteredMeetings, selectedTimes]);

    const columns = [
      {
        title: 'Company',
        dataIndex: 'companyName',
        key: 'companyName',
        render: (companyName: string, meeting: MeetingSearch) => {
          return <Link to={`company/${meeting.companyId}`}>{companyName}</Link>
        },
        sorter: {
          compare: (a: { companyName: string; }, b: { companyName: string; }) => a.companyName.localeCompare(b.companyName)
        }
      },
      {
        title: 'Meeting Timezone',
        dataIndex: 'timeZone',
        key: 'timeZone',
        render: (timeZone: TimeZone, meeting: MeetingSearch) => {
          return timeZone.name
        },
        sorter: {
          compare: (a: { timeZone: TimeZone; }, b: { timeZone: TimeZone; }) => a.timeZone.name.localeCompare(b.timeZone.name)
        }
      },
      {
        title: `Start Time (${timeZoneName})`,
        dataIndex: 'startTime',
        key: 'startTime',
        render: (date: string) => {
          return `${moment(date).format(GetDateFormateStr(1))} ${moment(date).toDate().toLocaleTimeString()}`;
        },
        sorter: {
          compare: (a, b) => {
              let d1 = a.startTime ?? new Date("01/01/1000");
              let d2 = b.startTime ?? new Date("01/01/1000");
              return moment(d1).diff(moment(d2));
          }
        },
      },
      {
        title: `End Time (${timeZoneName})`,
        dataIndex: 'endTime',
        key: 'endTime',
        render: (date: string) => {
          return `${moment(date).format(GetDateFormateStr(1))} ${moment(date).toDate().toLocaleTimeString()}`;
        },
        sorter: {
          compare: (a, b) => {
              let d1 = a.endTime ?? new Date("01/01/1000");
              let d2 = b.endTime ?? new Date("01/01/1000");
              return moment(d1).diff(moment(d2));
          }
        },
      },
    ]
    
    return (
      <Card title="Check Meetings">
        <Row gutter={[16, 16]}>
          <Col flex="auto">
            <Space>
              <DatePicker 
                value={selectedDate}
                onChange={(date) => setSelectedDates((date as Moment).startOf('day'))}
                allowClear={false}
              />
              <TimeZoneSelect
                timezoneId={timeZoneId}
                onChange={(val) => {
                  setTimeZoneId(val);
                }}
                span={6}
                // timeZoneRef={timeZoneRef}
                setTimeZoneName={setTimeZoneName}
              />
            </Space>
          </Col>
          <Col>
            <Space>
              <Input
                placeholder='Search Companies'
                onChange={(e) => setCompanySearch(e.target.value)}
              />
              <RangePicker
                value={selectedTimes}
                onChange={(val) => {
                  if(val?.length > 1 && val[0] > val[1]) {
                    setSelectedTimes([val[1], val[0]])
                  } else {
                    setSelectedTimes(val)
                  }
                }}
                minuteStep={15}
                format={'hh:mm'}
                use12Hours
              />
            </Space>
          </Col>
          <Col span={24}>
            <Title level={5}>Available Times:</Title>
            <List
              grid={{ gutter: 16, column: 4 }}
              dataSource={availableTimes}
              loading={meetingsQuery?.isLoading}
              locale={{
                emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={`No available times`} />
              }}
              renderItem={(item) => (
                <List.Item>
                  <AvailableTimeCard
                    startTime={moment(item[0]).toDate().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                    endTime={moment(item[1]).toDate().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                  />
                </List.Item>
              )}
            />
          </Col>
          <Col span={24}>
            <Title level={5}>Scheduled Meetings:</Title>
            <Table
              loading={meetingsQuery?.isLoading}
              columns={columns}
              size="middle"
              dataSource={filteredMeetings}
              rowKey={(val) => `${val.id}-${val.startTime}`}
              locale={{
                emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={`No meetings found`} />
              }}
            />
          </Col>
        </Row>
      </Card>
    );
}

export default MeetingsPage;
